import React, { useId } from "react";

/**
 * PRYMEBET custom icon.
 *
 * Renders one of the bespoke PRYMEBET SVGs from /public/icons as a monochrome
 * glyph that inherits the current text color. We use the CSS mask technique
 * (mask-image + background-color: currentColor) so every icon — stroke, fill,
 * or masked — themes uniformly:
 *   - inactive nav  -> muted neutral (text-muted-foreground)
 *   - active nav    -> PRYMEBET green  (text-pnr-green), or use the "-fill" name
 *   - PCoins      -> green   (<Icon name="pnrcoin" className="text-pnr-coin" />)
 *   - PCash       -> silver  (<Icon name="pnrcash" className="text-pnr-cash" />)
 *
 * EXCEPTION — pnrcoin / pnrcash: these two glyphs use an internal <mask> with a
 * shape that sits on the viewBox edge. Chrome's *production* CSS mask-image
 * compositor blanks those out (they render fine on the dev server), so the
 * balance/currency icons showed as empty space on pnrbet.com. We render just
 * those two as inline SVG instead — IDENTICAL artwork (same paths, verbatim
 * from the /public/icons files), only the drawn shape's fill is switched to
 * currentColor so it still themes. Everything else uses the mask path unchanged.
 * (If the canonical .svg files are ever redesigned, update these strings too.)
 *
 * Payment BRAND marks (Apple Pay, Google Pay, Visa, ...) are NOT in this set —
 * they are official third-party trademarks and drop in as their own asset files.
 */
const INLINE_GLYPHS = {
  pnrcoin:
    '<defs><mask id="__ID__" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24"><rect width="24" height="24" fill="#fff"></rect><g fill="none" stroke="#000" stroke-linecap="round" stroke-linejoin="round"><g stroke-width="1.3"><line x1="21.37" y1="14.51" x2="23.59" y2="15.11"></line><line x1="18.86" y1="18.86" x2="20.49" y2="20.49"></line><line x1="14.51" y1="21.37" x2="15.11" y2="23.59"></line><line x1="9.49" y1="21.37" x2="8.89" y2="23.59"></line><line x1="5.14" y1="18.86" x2="3.51" y2="20.49"></line><line x1="2.63" y1="14.51" x2="0.41" y2="15.11"></line><line x1="2.63" y1="9.49" x2="0.41" y2="8.89"></line><line x1="5.14" y1="5.14" x2="3.51" y2="3.51"></line><line x1="9.49" y1="2.63" x2="8.89" y2="0.41"></line><line x1="14.51" y1="2.63" x2="15.11" y2="0.41"></line><line x1="18.86" y1="5.14" x2="20.49" y2="3.51"></line><line x1="21.37" y1="9.49" x2="23.59" y2="8.89"></line></g><circle cx="12" cy="12" r="7.7" stroke-width="1.2"></circle><path d="M9.7 16.3V7.7h2.8a2.35 2.35 0 0 1 0 4.7H9.7" stroke-width="2"></path></g></mask></defs><circle cx="12" cy="12" r="11.2" fill="currentColor" mask="url(#__ID__)"></circle>',
  pnrcash:
    '<defs><mask id="__ID__" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24"><rect width="24" height="24" fill="#fff"></rect><circle cx="1" cy="12" r="2.6" fill="#000"></circle><circle cx="23" cy="12" r="2.6" fill="#000"></circle><g fill="none" stroke="#000" stroke-linecap="round" stroke-linejoin="round"><path d="M5.6 7.6v8.8M18.4 7.6v8.8" stroke-width="1.1" stroke-dasharray="1.4 1.6"></path><path d="M14.2 9.6c-.4-.8-1.2-1.2-2.2-1.2-1.3 0-2.2.7-2.2 1.7 0 2.3 4.5 1.2 4.5 3.8 0 1-1 1.8-2.3 1.8-1.1 0-2-.5-2.4-1.3M12 7v1.3M12 15.7V17" stroke-width="1.8"></path></g></mask></defs><rect x="0.8" y="4.8" width="22.4" height="14.4" rx="2.6" fill="currentColor" mask="url(#__ID__)"></rect>',
};

export function Icon({ name, size = 20, className = "", style, title, ...props }) {
  // Hooks must run unconditionally — compute an id even for mask-path icons.
  const rawId = useId();

  // Inline-SVG path for the two mask-unsafe currency glyphs (see note above).
  if (INLINE_GLYPHS[name]) {
    const maskId = `pnr-${name}-${rawId.replace(/[^a-zA-Z0-9_-]/g, "")}`;
    const __html = INLINE_GLYPHS[name].replace(/__ID__/g, maskId);
    return (
      <svg
        {...props}
        role="img"
        aria-label={title || name}
        aria-hidden={title ? undefined : true}
        className={`pnr-icon ${className}`}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        style={{ display: "inline-block", flex: "none", ...style }}
        dangerouslySetInnerHTML={{ __html }}
      />
    );
  }

  // Default path: CSS mask + currentColor (works for the other ~120 icons).
  const url = `${process.env.PUBLIC_URL || ""}/icons/${name}.svg`;
  return (
    <span
      role="img"
      aria-label={title || name}
      aria-hidden={title ? undefined : true}
      className={`pnr-icon ${className}`}
      style={{
        display: "inline-block",
        flex: "none",
        width: size,
        height: size,
        backgroundColor: "currentColor",
        WebkitMaskImage: `url("${url}")`,
        maskImage: `url("${url}")`,
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        maskPosition: "center",
        WebkitMaskSize: "contain",
        maskSize: "contain",
        ...style,
      }}
      {...props}
    />
  );
}

export default Icon;
